import type { RefObject } from 'react'
import { ProductList, usePaginationContext } from '@haus-storefront-react/product-list'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AriaLiveRegion, Typography } from '@components'
import { Button } from '@components/button/Button'
import { SkeletonLoader } from '@components/skeleton-loader/SkeletonLoader'
import styles from './Pagination.module.scss'

const MAX_VISIBLE_PAGES = 7

/** Delay before scrolling to the product list after a page change (allows navigation to settle). */
const SCROLL_DELAY_MS = 400

/**
 * Build the URL for a given page, preserving current query params.
 * @param page - 1-based page number.
 * @returns URL path with query string, or '#' when window is undefined.
 */
function buildPageHref(page: number): string {
  if (typeof window === 'undefined') return '#'

  const { pathname, search } = window.location

  const basePath = pathname.replace(/\/page\/\d+\/?$/, '')
  const trimmedBase =
    basePath === ''
      ? '/'
      : basePath.endsWith('/') && basePath !== '/'
        ? basePath.slice(0, -1)
        : basePath

  const targetPath =
    page <= 1 ? trimmedBase : `${trimmedBase === '/' ? '' : trimmedBase}/page/${page}/`

  return `${targetPath}${search}`
}

interface PaginationBodyProps {
  paginationButtonVariant: string
  scrollTargetRef?: RefObject<HTMLDivElement | null>
}

/**
 * Renders pagination using package compound components (Root, Pages with PrevLink, PageList, NextLink).
 * The package renders <a> for prev/next and page numbers; we pass className/children and add aria-live and styling.
 */
const PaginationBody = ({ paginationButtonVariant, scrollTargetRef }: PaginationBodyProps) => {
  const { t } = useTranslation()
  const [pageChangedAnnouncement, setPageChangedAnnouncement] = useState('')
  const lastSearchRef = useRef<string | null>(null)

  const {
    totalPages,
    canGoBack,
    canGoForward,
    getInfoProps,
    currentPage,
    goToPage,
    totalItems,
    infinitePagination,
  } = usePaginationContext()

  const buildHref = useCallback((page: number) => buildPageHref(page), [])

  const scrollToProductList = useCallback(
    (_page: number) => {
      if (!scrollTargetRef || typeof window === 'undefined') return
      window.setTimeout(() => {
        const container = scrollTargetRef.current
        if (container) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, SCROLL_DELAY_MS)
    },
    [scrollTargetRef],
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const { search } = window.location

    if (lastSearchRef.current === null) {
      lastSearchRef.current = search
      return
    }

    if (search === lastSearchRef.current) {
      return
    }

    lastSearchRef.current = search

    if (currentPage > 1) {
      goToPage(1)
    }
  }, [currentPage, goToPage])

  const handlePageClick = useCallback(
    (page: number, ctx: { totalPages: number }) => {
      setPageChangedAnnouncement(
        t('a11y.pagination.ariaLive.pageChanged', {
          currentPage: page,
          totalPages: ctx.totalPages,
        }),
      )
      scrollToProductList(page)
    },
    [t, scrollToProductList],
  )

  if (totalPages <= 1) {
    const { showing, total } = infinitePagination
      ? getInfoProps()
      : { showing: totalItems, total: totalItems }
    const singlePageMessage = t('a11y.pagination.ariaLive.pageChanged', {
      currentPage: 1,
      totalPages: 1,
    })
    return (
      <div className={styles.paginationRoot}>
        <Typography variant="body" className={styles.counter}>
          {t('pagination.showingOfTotal', {
            visibleItemCount: showing,
            totalItemCount: total,
          })}
        </Typography>
        <AriaLiveRegion message={singlePageMessage} />
      </div>
    )
  }

  return (
    <div className={styles.paginationRoot}>
      <ProductList.Pagination.Pages
        buildHref={buildHref}
        maxVisiblePages={MAX_VISIBLE_PAGES}
        onPageClick={handlePageClick}
      >
        {canGoForward && (
          <ProductList.Pagination.NextButton asChild>
            <Button
              className={styles.nextPageButton}
              data-button-variant={paginationButtonVariant.toLowerCase()}
              variant={paginationButtonVariant}
            >
              {t('pagination.next')}
            </Button>
          </ProductList.Pagination.NextButton>
        )}
        <nav className={styles.numberedPagination} aria-label={t('pagination.navigation')}>
          {canGoBack && (
            <ProductList.Pagination.Pages.PrevLink
              aria-label={t('pagination.previous')}
              className={styles.pageLink}
            >
              <ChevronLeftIcon aria-hidden width={20} height={20} />
            </ProductList.Pagination.Pages.PrevLink>
          )}
          <div className={styles.pageNumbers}>
            <ProductList.Pagination.Pages.PageList
              linkClassName={styles.pageLink}
              activeLinkClassName={styles.pageNumberActive}
              ellipsisClassName={styles.ellipsis}
            />
          </div>
          {canGoForward && (
            <ProductList.Pagination.Pages.NextLink
              aria-label={t('pagination.next')}
              className={styles.pageLink}
            >
              <ChevronRightIcon aria-hidden width={20} height={20} />
            </ProductList.Pagination.Pages.NextLink>
          )}
        </nav>
      </ProductList.Pagination.Pages>

      <AriaLiveRegion message={pageChangedAnnouncement} />
    </div>
  )
}

interface PaginationProps {
  isLoading?: boolean
  paginationButtonVariant?: string
  scrollTargetRef?: RefObject<HTMLDivElement | null>
}

/**
 * Pagination UI for the product list: page numbers, prev/next, and screen-reader announcements.
 * Built from @haus-storefront-react/product-list compound components; adds aria-live and styling.
 * @param isLoading - Whether the product list is loading (shows skeleton).
 * @param paginationButtonVariant - Theme button variant for the next page button (e.g. 'primary'); uses getButtonStyles and data-button-variant like Button.
 */
export const Pagination = ({
  isLoading = false,
  paginationButtonVariant = 'primary',
  scrollTargetRef,
}: PaginationProps) => {
  const { t } = useTranslation()
  const hasLoadedOnce = useRef(false)

  useEffect(() => {
    if (!isLoading) {
      hasLoadedOnce.current = true
    }
  }, [isLoading])

  const isInitialLoad = isLoading && !hasLoadedOnce.current

  if (isInitialLoad) {
    return (
      <div
        className={styles.paginationRoot}
        role="status"
        aria-label={t('a11y.pagination.aria.loadingLabel', 'Loading pagination')}
        aria-busy="true"
      >
        <SkeletonLoader isLoading>
          {({ skeletonAttributes }) => (
            <div
              className={styles.nextPageButton}
              {...skeletonAttributes({ minWidth: 80, minHeight: 44 })}
              aria-hidden
            >
              {'\u00A0'}
            </div>
          )}
        </SkeletonLoader>
        <SkeletonLoader isLoading>
          {({ skeletonAttributes }) => (
            <div
              className={styles.numberedPaginationSkeletonPlaceholder}
              {...skeletonAttributes({ minWidth: 280, minHeight: 44 })}
              aria-hidden
            />
          )}
        </SkeletonLoader>
      </div>
    )
  }

  return (
    <ProductList.Pagination.Root>
      <PaginationBody
        paginationButtonVariant={paginationButtonVariant}
        scrollTargetRef={scrollTargetRef}
      />
    </ProductList.Pagination.Root>
  )
}
